<template>
    <div class="documents">
        <UIPageHeader :title="$t('documents.title')" />

        <div class="documents__content">
            <div class="documents__card">
                <div class="documents__card-title">{{ $t('documents.incomeReport') }}</div>
                <div class="documents__card-desc">{{ $t('documents.incomeReportDesc') }}</div>

                <div class="documents__form">
                    <div class="documents__field">
                        <label class="documents__label">{{ $t('documents.periodStart') }}</label>
                        <input
                            class="documents__input"
                            type="text"
                            :placeholder="$t('documents.placeholderStart')"
                            v-model="start"
                        />
                    </div>
                    <div class="documents__field">
                        <label class="documents__label">{{ $t('documents.periodEnd') }}</label>
                        <input
                            class="documents__input"
                            type="text"
                            :placeholder="$t('documents.placeholderEnd')"
                            v-model="end"
                        />
                    </div>
                </div>

                <div v-if="error" class="documents__error">{{ error }}</div>

                <button
                    class="documents__btn"
                    :disabled="loading"
                    @click="downloadPdf"
                >
                    <span v-if="loading">{{ $t('documents.generating') }}</span>
                    <span v-else>{{ $t('documents.download') }}</span>
                </button>

                <div class="documents__note">{{ $t('documents.note') }}</div>
            </div>

            <div class="documents__card documents__card--wide">
                <div class="documents__card-title">{{ $t('documents.miningTitle') }}</div>
                <div class="documents__card-desc">{{ $t('documents.miningDesc') }}</div>

                <div class="documents__grid">
                    <div class="documents__field">
                        <label class="documents__label">{{ $t('documents.year') }}</label>
                        <select class="documents__input" v-model.number="reportYear">
                            <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                        </select>
                    </div>
                    <div class="documents__field">
                        <label class="documents__label">{{ $t('documents.month') }}</label>
                        <select class="documents__input" v-model.number="reportMonth">
                            <option v-for="(name, index) in monthNames" :key="name" :value="index + 1">{{ name }}</option>
                        </select>
                    </div>
                </div>

                <div v-if="reportError" class="documents__error">{{ reportError }}</div>

                <div class="documents__actions">
                    <button
                        class="documents__btn"
                        :disabled="reportLoading"
                        @click="fillReport"
                    >
                        <span v-if="reportLoading">{{ $t('documents.generating') }}</span>
                        <span v-else>{{ $t('documents.generate') }}</span>
                    </button>
                    <button
                        class="documents__btn documents__btn--ghost"
                        :disabled="!report || pdfLoading"
                        @click="downloadMiningPdf"
                    >
                        <span v-if="pdfLoading">{{ $t('documents.generating') }}</span>
                        <span v-else>{{ $t('documents.download') }}</span>
                    </button>
                    <button
                        class="documents__btn documents__btn--ghost"
                        :disabled="!report || xmlLoading"
                        @click="downloadMiningXml"
                    >
                        <span v-if="xmlLoading">{{ $t('documents.generating') }}</span>
                        <span v-else>{{ $t('documents.downloadXml') }}</span>
                    </button>
                </div>

                <template v-if="report">
                    <div class="documents__note">{{ $t('documents.missingNote') }}</div>

                    <div class="documents__section">{{ $t('documents.sectionMain') }}</div>
                    <div class="documents__grid">
                        <div class="documents__field">
                            <label class="documents__label">{{ $t('documents.year') }}</label>
                            <input class="documents__input" type="text" :value="report.year" readonly />
                        </div>
                        <div class="documents__field">
                            <label class="documents__label">{{ $t('documents.month') }}</label>
                            <input class="documents__input" type="text" :value="monthNames[report.month - 1]" readonly />
                        </div>
                        <div class="documents__field">
                            <label class="documents__label">{{ $t('documents.correction') }}</label>
                            <input class="documents__input" type="text" v-model="report.correction" />
                        </div>
                        <div class="documents__field">
                            <label class="documents__label">{{ $t('documents.taxOffice') }}</label>
                            <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(report.taxOffice) }" type="text" maxlength="4" v-model="report.taxOffice" />
                            <span v-if="isEmpty(report.taxOffice)" class="documents__hint">{{ $t('documents.taxOfficeHint') }}</span>
                        </div>
                        <div class="documents__field">
                            <label class="documents__label">{{ $t('documents.energy') }}</label>
                            <input class="documents__input" type="text" v-model="report.energyKwh" :placeholder="$t('documents.energyPh')" />
                        </div>
                    </div>

                    <div class="documents__section">{{ $t('documents.sectionContact') }}</div>
                    <div class="documents__grid">
                        <div class="documents__field">
                            <label class="documents__label">{{ $t('documents.phone') }}</label>
                            <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(report.phone) }" type="text" v-model="report.phone" />
                            <span v-if="isEmpty(report.phone)" class="documents__hint">{{ $t('documents.needValue') }}</span>
                        </div>
                        <div class="documents__field">
                            <label class="documents__label">{{ $t('documents.email') }}</label>
                            <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(report.email) }" type="text" v-model="report.email" />
                        </div>
                    </div>

                    <div class="documents__section">{{ $t('documents.sectionLegal') }}</div>
                    <div class="documents__grid">
                        <div class="documents__field documents__field--wide">
                            <label class="documents__label">{{ $t('documents.legalName') }}</label>
                            <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(report.legalName) }" type="text" v-model="report.legalName" />
                            <span v-if="isEmpty(report.legalName)" class="documents__hint">{{ $t('documents.needValue') }}</span>
                        </div>
                        <div class="documents__field">
                            <label class="documents__label">{{ $t('documents.inn') }}</label>
                            <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(report.inn) }" type="text" v-model="report.inn" />
                            <span v-if="isEmpty(report.inn)" class="documents__hint">{{ $t('documents.needValue') }}</span>
                        </div>
                        <div class="documents__field">
                            <label class="documents__label">{{ $t('documents.kpp') }}</label>
                            <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(report.kpp) }" type="text" v-model="report.kpp" />
                        </div>
                        <div class="documents__field">
                            <label class="documents__label">{{ $t('documents.ogrn') }}</label>
                            <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(report.ogrn) }" type="text" v-model="report.ogrn" />
                            <span v-if="isEmpty(report.ogrn)" class="documents__hint">{{ $t('documents.needValue') }}</span>
                        </div>
                        <div class="documents__field documents__field--wide">
                            <label class="documents__label">{{ $t('documents.legalAddress') }}</label>
                            <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(report.legalAddress) }" type="text" v-model="report.legalAddress" />
                            <span v-if="isEmpty(report.legalAddress)" class="documents__hint">{{ $t('documents.needValue') }}</span>
                        </div>
                    </div>

                    <div
                        v-for="(row, index) in report.currencies"
                        :key="'coin-' + index"
                        class="documents__block"
                    >
                        <div class="documents__section">{{ $t('documents.sectionCurrency') }}</div>
                        <div class="documents__grid">
                            <div class="documents__field documents__field--wide">
                                <label class="documents__label">{{ $t('documents.operationType') }}</label>
                                <select class="documents__input" v-model="report.operationType">
                                    <option value="ON_POOL">{{ $t('documents.onPool') }}</option>
                                    <option value="CREDITED">{{ $t('documents.credited') }}</option>
                                </select>
                            </div>
                            <div class="documents__field">
                                <label class="documents__label">{{ $t('documents.currency') }}</label>
                                <input class="documents__input" type="text" v-model="row.currency" />
                            </div>
                            <div class="documents__field">
                                <label class="documents__label">{{ report.operationType === 'CREDITED' ? $t('documents.creditedAmount') : $t('documents.minedAmount') }}</label>
                                <input class="documents__input" type="text" v-model="row.amount" />
                            </div>
                            <div class="documents__field documents__field--wide">
                                <label class="documents__label">{{ $t('documents.creditAddress') }}</label>
                                <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(report.creditAddress) }" type="text" maxlength="128" v-model="report.creditAddress" />
                                <span v-if="report.creditAddressSource === 'withdrawal'" class="documents__hint">{{ $t('documents.creditFromWithdrawal') }}</span>
                                <span v-else-if="isEmpty(report.creditAddress)" class="documents__hint">{{ $t('documents.needValue') }}</span>
                                <button class="documents__btn documents__btn--ghost" type="button" @click="saveCreditAddress">
                                    {{ $t('documents.creditSave') }}
                                </button>
                            </div>
                            <div class="documents__field documents__field--wide">
                                <label class="documents__label">{{ $t('documents.poolAddress') }}</label>
                                <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(report.poolAddress) }" type="text" :value="report.poolAddress" readonly />
                                <span v-if="isEmpty(report.poolAddress)" class="documents__hint">{{ $t('documents.needValue') }}</span>
                            </div>
                            <div class="documents__field documents__field--wide">
                                <label class="documents__label">{{ $t('documents.watcher') }}</label>
                                <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(report.watcherLink) }" type="text" v-model="report.watcherLink" />
                            </div>
                        </div>
                    </div>

                    <div class="documents__section">{{ $t('documents.equipment') }}</div>
                    <div
                        v-for="(item, index) in report.equipment"
                        :key="'eq-' + index"
                        class="documents__block"
                    >
                        <div class="documents__grid">
                            <div class="documents__field">
                                <label class="documents__label">{{ $t('documents.worker') }}</label>
                                <input class="documents__input" type="text" :value="item.workerName" readonly />
                            </div>
                            <div class="documents__field">
                                <label class="documents__label">{{ $t('documents.nominal') }}</label>
                                <input class="documents__input" type="text" :value="item.nominalTh" readonly />
                            </div>
                            <div class="documents__field">
                                <label class="documents__label">{{ $t('documents.brand') }}</label>
                                <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(item.brand) }" type="text" v-model="item.brand" />
                                <span v-if="isEmpty(item.brand)" class="documents__hint">{{ $t('documents.needValue') }}</span>
                            </div>
                            <div class="documents__field">
                                <label class="documents__label">{{ $t('documents.model') }}</label>
                                <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(item.model) }" type="text" v-model="item.model" />
                                <span v-if="isEmpty(item.model)" class="documents__hint">{{ $t('documents.needValue') }}</span>
                            </div>
                            <div class="documents__field">
                                <label class="documents__label">{{ $t('documents.serial') }}</label>
                                <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(item.serial) }" type="text" v-model="item.serial" />
                                <span v-if="isEmpty(item.serial)" class="documents__hint">{{ $t('documents.needValue') }}</span>
                            </div>
                            <div class="documents__field">
                                <label class="documents__label">{{ $t('documents.hours') }}</label>
                                <input class="documents__input" :class="{ 'documents__input--empty': isEmpty(item.hours) }" type="text" v-model="item.hours" />
                                <span v-if="isEmpty(item.hours)" class="documents__hint">{{ $t('documents.needValue') }}</span>
                            </div>
                            <div class="documents__field">
                                <label class="documents__label">{{ $t('documents.stratumHours') }}</label>
                                <input class="documents__input" type="text" :value="item.stratumHours || '0'" readonly />
                            </div>
                        </div>
                    </div>
                    <button class="documents__btn documents__btn--ghost" type="button" @click="addEquipment">
                        {{ $t('documents.addEquipment') }}
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import UIPageHeader from "@/components/ui/page-header/page-header.vue";
import http from "@/api/http";

export default {
    name: "Documents",
    components: { UIPageHeader },
    data() {
        const now = new Date();
        const prev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
        return {
            start: "",
            end: "",
            loading: false,
            error: null,
            reportYear: prev.getUTCFullYear(),
            reportMonth: prev.getUTCMonth() + 1,
            reportLoading: false,
            pdfLoading: false,
            xmlLoading: false,
            reportError: null,
            report: null,
        };
    },
    computed: {
        years() {
            const current = new Date().getUTCFullYear();
            return [current - 2, current - 1, current, current + 1];
        },
        monthNames() {
            const locale = this.$i18n.locale;
            const code = locale && typeof locale === "object" ? locale.value : locale;
            if (code === "en") {
                return ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
            }
            return ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
        },
    },
    methods: {
        isEmpty(value) {
            return value == null || String(value).trim() === "";
        },
        addEquipment() {
            this.report.equipment.push({
                workerName: "",
                nominalTh: "",
                brand: "",
                model: "",
                serial: "",
                hours: "",
                stratumHours: "0",
            });
        },
        async saveCreditAddress() {
            if (!this.report) {
                return;
            }
            this.reportError = null;
            try {
                const response = await http.post("/documents/credit-address", {
                    address: (this.report.creditAddress || "").trim(),
                });
                const body = response.data;
                if (!body || body.status !== "success") {
                    this.reportError = (body && body.message) || this.$t("documents.error");
                    return;
                }
                this.report.creditAddressSource = this.isEmpty(this.report.creditAddress) ? "empty" : "saved";
            } catch (e) {
                this.reportError = this.$t("documents.error");
            }
        },
        async fillReport() {
            this.reportError = null;
            this.reportLoading = true;
            try {
                const response = await http.post("/documents/mining-report", {
                    year: this.reportYear,
                    month: this.reportMonth,
                });
                const body = response.data;
                if (!body || body.status !== "success" || !body.data) {
                    this.reportError = (body && body.message) || this.$t("documents.error");
                    return;
                }
                const draft = body.data;
                if (!draft.equipment || !draft.equipment.length) {
                    draft.equipment = [{
                        workerName: "",
                        nominalTh: "",
                        brand: "",
                        model: "",
                        serial: "",
                        hours: "",
                        stratumHours: "0",
                    }];
                }
                if (draft.taxOffice == null) draft.taxOffice = "";
                if (draft.energyKwh == null) draft.energyKwh = "";
                this.report = draft;
            } catch {
                this.reportError = this.$t("documents.error");
            } finally {
                this.reportLoading = false;
            }
        },
        async downloadMiningPdf() {
            if (!this.report) {
                return;
            }
            this.reportError = null;
            this.pdfLoading = true;
            try {
                const response = await http.post(
                    "/documents/mining-report/pdf",
                    this.report,
                    { responseType: "blob" }
                );
                const blob = new Blob([response.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                const disposition = response.headers["content-disposition"];
                const match = disposition && disposition.match(/filename="(.+)"/);
                link.download = match ? match[1] : "mining-report.pdf";
                link.click();
                URL.revokeObjectURL(url);
            } catch {
                this.reportError = this.$t("documents.error");
            } finally {
                this.pdfLoading = false;
            }
        },
        async blobErrorMessage(error) {
            const data = error && error.response && error.response.data;
            if (data instanceof Blob) {
                try {
                    const json = JSON.parse(await data.text());
                    if (json && json.message) return json.message;
                } catch {
                    return this.$t("documents.error");
                }
            }
            return this.$t("documents.error");
        },
        async downloadMiningXml() {
            if (!this.report) {
                return;
            }
            this.reportError = null;
            this.xmlLoading = true;
            try {
                const response = await http.post(
                    "/documents/mining-report/xml",
                    this.report,
                    { responseType: "blob" }
                );
                const blob = new Blob([response.data], { type: "application/zip" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                const disposition = response.headers["content-disposition"];
                const match = disposition && disposition.match(/filename="(.+)"/);
                link.download = match ? match[1] : "mining-report.zip";
                link.click();
                URL.revokeObjectURL(url);
            } catch (error) {
                this.reportError = await this.blobErrorMessage(error);
            } finally {
                this.xmlLoading = false;
            }
        },
        async downloadPdf() {
            this.error = null;
            this.loading = true;
            try {
                const response = await http.post(
                    "/documents/income",
                    { start: this.start || null, end: this.end || null },
                    { responseType: "blob" }
                );
                const blob = new Blob([response.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                const disposition = response.headers["content-disposition"];
                const match = disposition && disposition.match(/filename="(.+)"/);
                link.download = match ? match[1] : "income-report.pdf";
                link.click();
                URL.revokeObjectURL(url);
            } catch {
                this.error = this.$t("documents.error");
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss">
.documents {
    display: flex;
    flex-direction: column;

    .documents__content {
        padding: 24px 0 40px 10px;
        display: flex;
        flex-direction: column;
        grid-row-gap: 24px;
    }

    .documents__card {
        background: $white;
        border-radius: 20px;
        padding: 32px;
        max-width: 520px;
        display: flex;
        flex-direction: column;
        grid-row-gap: 16px;
    }

    .documents__card--wide {
        max-width: 860px;
    }

    .documents__section {
        @include golos-bold;
        font-size: 15px;
        color: $txt-main;
        margin-top: 8px;
    }

    .documents__block {
        border: 1px solid $gray;
        border-radius: 14px;
        padding: 16px;
    }

    .documents__grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
    }

    .documents__field--wide {
        grid-column: 1 / -1;
    }

    .documents__hint {
        @include golos-regular;
        font-size: 11px;
        color: #c47b00;
    }

    .documents__card-title {
        @include golos-bold;
        font-size: 18px;
        color: $txt-main;
    }

    .documents__card-desc {
        @include golos-regular;
        font-size: 13px;
        color: $txt-light;
        line-height: 1.6;
    }

    .documents__form {
        display: flex;
        flex-direction: column;
        grid-row-gap: 12px;
    }

    .documents__field {
        display: flex;
        flex-direction: column;
        grid-row-gap: 6px;
    }

    .documents__label {
        @include golos-medium;
        font-size: 11px;
        color: $txt-light;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .documents__input {
        @include golos-regular;
        font-size: 14px;
        color: $txt-main;
        border: 1px solid $gray;
        border-radius: 10px;
        padding: 10px 14px;
        outline: none;
        transition: border-color $transition;
        background: $bg-light;
        width: 100%;
        box-sizing: border-box;

        &:focus {
            border-color: $main;
        }

        &--empty {
            border-color: #f0c36a;
            background: #fffaf0;
        }

        &::placeholder {
            color: $txt-light;
            opacity: 0.6;
        }
    }

    .documents__error {
        @include golos-regular;
        font-size: 13px;
        color: #e53935;
    }

    .documents__actions {
        display: flex;
        gap: 12px;

        .documents__btn {
            width: auto;
            flex: 1;
        }
    }

    .documents__btn {
        @include button-reset;
        @include golos-bold;
        font-size: 14px;
        color: $white;
        background: $main;
        border-radius: 12px;
        padding: 12px 24px;
        cursor: pointer;
        transition: opacity $transition;
        text-align: center;
        width: 100%;

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        &:hover:not(:disabled) {
            opacity: 0.85;
        }
    }

    .documents__btn--ghost {
        color: $main;
        background: $bg-light;
        width: auto;
        align-self: flex-start;
    }

    .documents__note {
        @include golos-regular;
        font-size: 11px;
        color: $txt-light;
        line-height: 1.5;
    }
}

@media screen and (max-width: 768px) {
    .documents {
        .documents__grid {
            grid-template-columns: 1fr;
        }

        .documents__card,
        .documents__card--wide {
            max-width: none;
            padding: 20px;
        }
    }
}
</style>
